<script setup lang="ts">
import { onMounted, useTemplateRef } from "vue";

import { windowScaleFromRelative } from "@/math/coordinate-transform";
import { sizedRectangle } from "@/math/coordinates";
import { fakeZoom } from "@/model/Settings";
import type { Color } from "@/model/baseTypes";
import type { Board } from "@/model/board";
import color from "@/model/color.module.scss";
import type { SizedRectangle } from "@/model/coordinates";
import { windowLen } from "@/model/coordinates";
import { isFaded } from "@/model/markMode";
import { isDependency } from "@/model/stickyType";
import { useBoardStore } from "@/store/board";
import { cardColor } from "@/store/card";
import type { StickyNoteTextAlignment } from "@/store/clientSettings";
import { useClientSettingsStore } from "@/store/clientSettings";
import { useZoomStore } from "@/store/zoom";
import { contrastCssColor, toCssColor } from "@/utils/color";
import { optimalDisplayFontSize } from "@/utils/text/fontSizeOptimizer";

import type { ZoomLayer } from "./ZoomLayer";
import type { RelativeCardRect } from "./internal/zoomUtils";
import { getRelativeCardRects } from "./internal/zoomUtils";

defineExpose<ZoomLayer>({ paintCards, hide });

interface Font {
  factor: number;
  family: string;
  weight: string;
  lineHeight: number;
}

// TODO: write a vite plugin to import this from the css custom properties file
// https://rentouch.atlassian.net/browse/REN-12514

// black-alpha-20 - hardcoding since the css custom properties are not available in the canvas
const shadowColor = "rgba(0, 0, 0, 0.2)";
const borderColor = "#3234e4";
// Where to draw text in the card, based on user-chosen text alignment
const alignmentFactor = { left: 0.037, center: 0.5, right: 0.963 };

const canvasRef = useTemplateRef("canvas");

let ctx: CanvasRenderingContext2D;
let textAlignment: StickyNoteTextAlignment = "left";

onMounted(() => {
  ctx = canvasRef.value!.getContext("2d")!;
});

// paint a simplified version of a board's stickies
function paintCards(board: Board) {
  const canvasSize = windowLen(canvasRef.value!.width, canvasRef.value!.height);

  textAlignment = useClientSettingsStore().textAlignment || "left";

  ctx.clearRect(0, 0, canvasSize.x, canvasSize.y);
  show();
  drawShapes();
  drawCards();

  function drawShapes() {
    ctx.strokeStyle = color.borderMenuColor;
    ctx.lineWidth = 12;
    ctx.beginPath();
    for (const shape of board.shapes) {
      switch (shape.type) {
        case "line": {
          const from = windowScaleFromRelative(shape.p0, { board: canvasSize });
          const to = windowScaleFromRelative(shape.p1, { board: canvasSize });
          ctx.moveTo(from.x, from.y);
          ctx.lineTo(to.x, to.y);
          break;
        }
      }
    }
    ctx.stroke();
  }

  function drawCards() {
    let font: Font | null = null;
    for (const relativeRect of getRelativeCardRects(board)) {
      const cardElem = document.getElementById(relativeRect.id);
      // in some rare timing occasions, cardElem might be undefined
      if (cardElem) {
        const textElem = cardElem.querySelector<HTMLElement>(".sticky-note-text-wrapper>*")!;
        font = font || calcFont(textElem);
        drawCard(relativeRect, textElem, font);
      }
    }
  }

  function drawCard(rect: RelativeCardRect, textElem: HTMLElement, font: Font) {
    const card = board.cards[rect.id];

    const size = sizedRectangle({
      from: windowScaleFromRelative(rect.p0, { board: canvasSize }),
      size: windowScaleFromRelative(rect.size, { board: canvasSize }),
    });
    const currentColor = cardColor(card.data, board);
    drawCardRect(size, currentColor);

    const fontData = optimalDisplayFontSize(
      textElem,
      card.data.text,
      useClientSettingsStore().stickyFont,
      { isDependency: isDependency(card.data) },
    );
    drawCardText(
      size,
      currentColor,
      fontData.textLines,
      applyFont(font, textElem),
      font.lineHeight,
    );

    drawCardOverlays(
      size,
      useBoardStore().currentBoard().cardSize.factor,
      useBoardStore().isStickySelected(card.data.id),
      isFaded(card.meta.mark),
    );
  }
}

function show() {
  canvasRef.value!.style.display = "block";
}

function hide() {
  canvasRef.value!.style.display = "none";
}

function calcFont(textElem: HTMLElement): Font {
  const style = window.getComputedStyle(textElem);
  const zoomFactor = fakeZoom / useZoomStore().factor;
  const elementScale =
    (zoomFactor * textElem.getBoundingClientRect().width) / parseInt(style.width);
  const relativeCanvasSize = canvasRef.value!.width / canvasRef.value!.offsetWidth;
  const relativeFontSize = parseFloat(style.fontSize) / parseFloat(textElem.style.fontSize);
  return {
    factor: relativeCanvasSize * relativeFontSize * elementScale,
    family: style.fontFamily,
    weight: style.fontWeight,
    lineHeight: parseFloat(style.lineHeight) / parseFloat(style.fontSize),
  };
}

function applyFont(font: Font, textElem: HTMLElement): number {
  const fontSize = parseFloat(textElem.style.fontSize) * font.factor;
  ctx.font = `${font.weight} ${fontSize}px ${font.family}`;
  return fontSize;
}

function resetShadow() {
  ctx.shadowBlur = 0;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 0;
}

function drawCardRect(rect: SizedRectangle, backgroundColor: Color) {
  ctx.fillStyle = toCssColor(backgroundColor);

  ctx.shadowColor = shadowColor;
  ctx.shadowBlur = Math.round(rect.size.y / 30); // this should give us ~8px shadow blur

  ctx.fillRect(rect.p0.x, rect.p0.y, rect.size.x, rect.size.y);

  // Reset shadow for other elements
  resetShadow();
}

function drawCardOverlays(
  rect: SizedRectangle,
  cardSize: number,
  selected: boolean,
  faded: boolean,
) {
  if (selected) {
    // Set the properties for the border
    ctx.strokeStyle = borderColor;
    ctx.lineWidth = cardSize * 1.5;

    // Draw the border around the filled rectangle
    ctx.strokeRect(rect.p0.x, rect.p0.y, rect.size.x, rect.size.y);
  }
  if (faded) {
    ctx.fillStyle = color.lowlight;
    ctx.fillRect(rect.p0.x, rect.p0.y, rect.size.x, rect.size.y);
  }
}

function drawCardText(
  rect: SizedRectangle,
  color: Color,
  lines: string[],
  fontSize: number,
  lineHeight: number,
) {
  ctx.fillStyle = contrastCssColor(color);
  ctx.textAlign = textAlignment;
  const left = rect.p0.x + alignmentFactor[textAlignment] * rect.size.x;
  const top = rect.p0.y + 0.155 * rect.size.y + 0.95 * fontSize;
  let y = 0;
  for (const line of lines) {
    ctx.fillText(line, left, top + y * lineHeight * fontSize);
    y++;
  }
}
</script>

<template>
  <canvas
    id="zoom-canvas"
    ref="canvas"
    width="9600"
    height="5400"
    aria-hidden="true"
    tabindex="-1"
  />
</template>

<style lang="scss">
@use "@/styles/variables";
@use "@/styles/z-index";

#zoom-canvas {
  display: none;
  pointer-events: none;
  position: absolute;
  width: 100% * variables.$fake-zoom;
  height: 100% * variables.$fake-zoom;
  z-index: z-index.$zoom;
}
</style>
